import { useEffect, useState } from 'react';

import { useUserExperience } from '~/features/user/hooks/use-user-experience';
import { Community } from '~/features/user/models/user-experience';
import { useClientRender } from '~/hooks/use-client-render';

const VISITED_COMMUNITIES_STORAGE_KEY = 'udacity-visited-community-urls';

function getVisitedCommunitiesFromLocalStorage(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }
  try {
    const stored = localStorage.getItem(VISITED_COMMUNITIES_STORAGE_KEY);
    if (!stored) {
      return new Set();
    }
    const parsed = JSON.parse(stored) as string[];
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

export type CommunityWithVisited = Community & {
  userHasVisited: boolean;
};

export function useCommunities() {
  const clientRender = useClientRender();
  const { userExperience } = useUserExperience();
  const [visitedCommunities, setVisitedCommunities] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (clientRender) {
      setVisitedCommunities(getVisitedCommunitiesFromLocalStorage());
    }
  }, [clientRender]);

  const setVisited = (communityUrl: string) => {
    const visited = getVisitedCommunitiesFromLocalStorage();
    visited.add(communityUrl);
    localStorage.setItem(VISITED_COMMUNITIES_STORAGE_KEY, JSON.stringify(Array.from(visited)));
    setVisitedCommunities(visited);
  };

  const allCommunities = (userExperience?.communities || []).map((community: Community) => {
    return {
      ...community,
      userHasVisited: visitedCommunities.has(community.url),
    };
  });

  const nonVisitedCommunities = allCommunities.filter((community: CommunityWithVisited) => !community.userHasVisited);

  const firstNonVisitedCommunityUrl =
    nonVisitedCommunities.length > 0
      ? nonVisitedCommunities[0].url
      : allCommunities.length > 0
        ? allCommunities[0].url
        : '';

  return {
    allCommunities,
    firstNonVisitedCommunityUrl,
    setVisited,
  };
}
